// src/pages/CompanyAdmin.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/apiService';
import '../styles/CompanyAdmin.css';

const CompanyAdmin = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingCompanies, setPendingCompanies] = useState([]);
  const [admittedCompanies, setAdmittedCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (activeTab === 'pending') {
      fetchPendingCompanies();
    } else {
      fetchAdmittedCompanies();
    }
  }, [activeTab]);

  const fetchPendingCompanies = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiService.get('/api/Admit/pending');
      setPendingCompanies(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load pending companies: ' + err.message);
      setPendingCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdmittedCompanies = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiService.get('/api/Admit/admitted');
      setAdmittedCompanies(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load admitted companies: ' + err.message);
      setAdmittedCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (companyId) => {
    if (!window.confirm('Are you sure you want to approve this company?')) {
      return;
    }

    try {
      await apiService.post(`/api/Admit/${companyId}`);
      setSuccessMessage('Company approved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchPendingCompanies();
    } catch (err) {
      setError('Failed to approve company: ' + err.message);
    }
  };

  const handleReject = async () => {
    if (!selectedCompany || !rejectReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    try {
      await apiService.post(`/api/Admit/${selectedCompany.id}/reject`, rejectReason);
      setSuccessMessage('Company rejected successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setShowRejectModal(false);
      setRejectReason('');
      setSelectedCompany(null);
      fetchPendingCompanies();
    } catch (err) {
      setError('Failed to reject company: ' + err.message);
    }
  };

  const handleViewDetails = async (companyId) => {
    try {
      const data = await apiService.get(`/api/Admit/${companyId}`);
      setSelectedCompany(data);
    } catch (err) {
      setError('Failed to load company details: ' + err.message);
    }
  };

  const openRejectModal = (company) => {
    setSelectedCompany(company);
    setShowRejectModal(true);
    setRejectReason('');
    setError('');
  };

  const closeRejectModal = () => {
    setShowRejectModal(false);
    setSelectedCompany(null);
    setRejectReason('');
    setError('');
  };

  const CompanyCard = ({ company, isPending }) => (
    <div className="company-card">
      <div className="company-card-header">
        <h3>{company.companyName || company.name || 'Unnamed Company'}</h3>
        <span className={`status-badge ${isPending ? 'pending' : 'approved'}`}>
          {isPending ? 'Pending' : 'Approved'}
        </span>
      </div>
      
      <div className="company-info">
        <div className="info-row">
          <span className="label">Email:</span>
          <span className="value">{company.companyEmail || company.email || 'N/A'}</span>
        </div>
        <div className="info-row">
          <span className="label">Phone:</span>
          <span className="value">{company.companyPhone || company.phone || 'N/A'}</span>
        </div>
        <div className="info-row">
          <span className="label">Industry:</span>
          <span className="value">{company.industry || 'N/A'}</span>
        </div>
        <div className="info-row">
          <span className="label">Location:</span>
          <span className="value">
            {company.city && company.province 
              ? `${company.city}, ${company.province}` 
              : company.city || company.province || 'N/A'}
          </span>
        </div>
        <div className="info-row">
          <span className="label">Registered:</span>
          <span className="value">
            {company.createdAt 
              ? new Date(company.createdAt).toLocaleDateString() 
              : 'N/A'}
          </span>
        </div>
      </div>

      {isPending && (
        <div className="company-actions">
          <button 
            className="btn-approve"
            onClick={() => handleApprove(company.id || company.companyId)}
          >
            ✓ Approve
          </button>
          <button 
            className="btn-reject"
            onClick={() => openRejectModal(company)}
          >
            ✕ Reject
          </button>
          <button 
            className="btn-details"
            onClick={() => handleViewDetails(company.id || company.companyId)}
          >
            View Details
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="company-admin-container">
      <div className="admin-header">
        <div>
          <h1>Company Management</h1>
          <p className="subtitle">Approve or reject company registrations</p>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {successMessage && <div className="success-banner">{successMessage}</div>}

      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          Pending Applications
          {pendingCompanies.length > 0 && (
            <span className="badge">{pendingCompanies.length}</span>
          )}
        </button>
        <button 
          className={`tab ${activeTab === 'admitted' ? 'active' : ''}`}
          onClick={() => setActiveTab('admitted')}
        >
          Approved Companies
          {admittedCompanies.length > 0 && (
            <span className="badge">{admittedCompanies.length}</span>
          )}
        </button>
      </div>

      <div className="tab-content">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading companies...</p>
          </div>
        ) : (
          <>
            {activeTab === 'pending' && (
              <div className="companies-grid">
                {pendingCompanies.length === 0 ? (
                  <div className="empty-state">
                    <p>No pending applications</p>
                  </div>
                ) : (
                  pendingCompanies.map((company) => (
                    <CompanyCard 
                      key={company.id || company.companyId} 
                      company={company} 
                      isPending={true} 
                    />
                  ))
                )}
              </div>
            )}

            {activeTab === 'admitted' && (
              <div className="companies-grid">
                {admittedCompanies.length === 0 ? (
                  <div className="empty-state">
                    <p>No approved companies yet</p>
                  </div>
                ) : (
                  admittedCompanies.map((company) => (
                    <CompanyCard 
                      key={company.id || company.companyId} 
                      company={company} 
                      isPending={false} 
                    />
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="modal-overlay" onClick={closeRejectModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Reject Company Application</h2>
              <button className="modal-close" onClick={closeRejectModal}>&times;</button>
            </div>

            <div className="modal-body">
              <p><strong>Company:</strong> {selectedCompany?.companyName || selectedCompany?.name}</p>
              <p><strong>Email:</strong> {selectedCompany?.companyEmail || selectedCompany?.email}</p>
              
              <div className="form-group">
                <label htmlFor="rejectReason">Reason for Rejection *</label>
                <textarea
                  id="rejectReason"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Please provide a reason for rejecting this application..."
                  rows="5"
                />
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn-cancel" onClick={closeRejectModal}>
                Cancel
              </button>
              <button className="btn-confirm-reject" onClick={handleReject}>
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyAdmin;